/**
 * The Notebook — permanent full-fidelity week archive (V2 §11, register
 * #34). Preserve-first: `bindWeek` captures the ENTIRE week (open +
 * completed tasks with steps, notes, tags, dates) at the moment Start
 * New Week closes it — the data rolloverWeek otherwise destroys.
 *
 * VOCABULARY FREEZE (founder requirement, 14 Aug 2026): tags and roles
 * change over a lifetime, and `deleteTag` hard-removes them from the
 * live registry. Search over history must never fail because today's
 * rail no longer lists a tag — so every snapshot embeds the FULL tag
 * vocabulary as it stood at bind time (names, kinds, colors). Deleting
 * a tag hides it from current options only; the Notebook keeps it as a
 * searchable, renderable term forever.
 *
 * Pure and total: no I/O here. Storage/sync live in the store and
 * firebaseSync; search and project threads arrive with the Notebook UI
 * wave — this module is the substrate they (and DeliverIt) will read.
 */

import type { Task, WeekRecord } from '../types';
import type { Tag } from './tags';

/** A closed week, exactly as it looked when bound. Text-only (scans are
 *  never part of Task), so a year of weeks is a few hundred KB. */
export interface WeekSnapshot {
  /** Same id as the WeekRecord it accompanies — one shelf, one key. */
  id: string;
  label: string;
  endedAt: number;
  /** Every task on the board at bind time — completed AND carried. */
  tasks: Task[];
  /** The complete tag registry at bind time — including tags no task
   *  used that week, so retired vocabulary stays offered by search. */
  tags: Record<string, Tag>;
}

/** Bind the closing week. Deep-copies tasks AND the tag vocabulary so
 *  later mutations/deletions on the live board can never rewrite (or
 *  orphan) history. */
export function bindWeek(
  record: WeekRecord,
  tasks: Task[],
  tags: Record<string, Tag>,
): WeekSnapshot {
  return {
    id: record.id,
    label: record.label,
    endedAt: record.endedAt,
    tasks: tasks.map((t) => ({
      ...t,
      steps: t.steps.map((s) => ({ ...s })),
      ...(t.tags ? { tags: [...t.tags] } : {}),
    })),
    tags: Object.fromEntries(
      Object.entries(tags).map(([id, tag]) => [id, { ...tag }]),
    ),
  };
}

/** Completed tasks of a bound week (the "what got done" reading view). */
export function completedOf(snap: WeekSnapshot): Task[] {
  return snap.tasks.filter((t) => t.done);
}

/** Open-at-close tasks (carried into the next week at bind time). */
export function carriedOf(snap: WeekSnapshot): Task[] {
  return snap.tasks.filter((t) => !t.done);
}

/** Resolve a bound task's tags against the snapshot's OWN frozen legend
 *  — never the live registry, which may have deleted/renamed them. */
export function tagsOf(snap: WeekSnapshot, task: Task): Tag[] {
  return (task.tags ?? [])
    .map((id) => snap.tags?.[id])
    .filter((t): t is Tag => Boolean(t));
}
